import * as React from 'react';
import { Translate } from 'react-redux-i18n';

import { Section, ButtonsWrapper, Button, SubScript, SuperScript } from './styles';
import { Label } from '../styles';
import Select from '../Select';
import { ListTypeOption } from '../Select/styles';
import { TEXT_ALIGN, TEXT_DECORATION, TEXT_TRANSFORM, LIST_TYPE, TEXT_SCRIPT } from '../../RichTextEditor/helpers/constants';
import { VerticalAlignClassName } from '../../../../containers/Editor/constants';
import Utils from '../../../../utils';

interface TextPanelActions {
	onFontFamilyChange: () => void;
	onFontSizeChange: () => void;
	onColorChange: () => void;
	onLineHeightChange: () => void;
	onLetterSpacingChange: () => void;
	onFontWeightClick: () => void;
	onFontStyleClick: () => void;
	onTextDecorationChange: (param: TEXT_DECORATION) => void;
	onTextTransformChange: (param: TEXT_TRANSFORM) => void;
	onTextScriptChange: (param: TEXT_SCRIPT) => void;
	onTextAlignChange: (param: TEXT_ALIGN) => void;
	onVerticalAlignChange: (param: VerticalAlignClassName | '') => void;
	onListTypeChange: (param: LIST_TYPE) => void;
}

interface TextPanelProps {
	fontFamily: string;
	fontSize: string;
	color: string;
	lineHeight: string;
	letterSpacing: string;
	isBoldActive: boolean;
	isItalicActive: boolean;
	isUnderlineActive: boolean;
	isLineThroughActive: boolean;
	isCapitalizeActive: boolean;
	isUpperCaseActive: boolean;
	isLowerCaseActive: boolean;
	isSuperScriptActive: boolean;
	isSubScriptActive: boolean;
	isTextAlignLeft: boolean;
	isTextAlignCenter: boolean;
	isTextAlignRight: boolean;
	isTextAlignJustify: boolean;
	isVerticalAlignTop: boolean;
	isVerticalAlignCenter: boolean;
	isVerticalAlignBottom: boolean;
	listType: LIST_TYPE;
	hasColorPicker: boolean;
	fontFamilyOptions: {
		values: string[];
		label: React.ReactNode;
	}[];
	fontSizeOptions: {
		values: string[];
		label: string;
	}[];
	colorOptions: {
		values: string[];
		label: React.ReactNode;
	}[];
	lineHeightOptions: {
		values: string[];
		label: string;
	}[];
	letterSpacingOptions: {
		values: string[];
		label: string;
	}[];
	actions: TextPanelActions;
}

const listTypeOptions = [
	{
		label: (
			<ListTypeOption>
				<Translate value="messages.bulleted" />
				{Utils.getSvgIcon('iconset_uprez_bulleted_list')}
			</ListTypeOption>
		),
		values: [LIST_TYPE.UNORDERED],
	},
	{
		label: (
			<ListTypeOption>
				<Translate value="messages.numbered" />
				{Utils.getSvgIcon('iconset_uprez_numbered_list')}
			</ListTypeOption>
		),
		values: [LIST_TYPE.ORDERED],
	},
	{
		label: <Translate value="messages.none" />,
		values: [LIST_TYPE.NONE],
	},
];

const TextPanel = ({
	fontFamily,
	fontSize,
	color,
	lineHeight,
	letterSpacing,
	isBoldActive,
	isItalicActive,
	isUnderlineActive,
	isLineThroughActive,
	isCapitalizeActive,
	isUpperCaseActive,
	isLowerCaseActive,
	isSuperScriptActive,
	isSubScriptActive,
	isTextAlignLeft,
	isTextAlignCenter,
	isTextAlignRight,
	isTextAlignJustify,
	isVerticalAlignTop,
	isVerticalAlignCenter,
	isVerticalAlignBottom,
	listType,
	hasColorPicker,
	fontFamilyOptions,
	fontSizeOptions,
	colorOptions,
	lineHeightOptions,
	letterSpacingOptions,
	actions: {
		onFontFamilyChange,
		onFontSizeChange,
		onColorChange,
		onLineHeightChange,
		onLetterSpacingChange,
		onFontWeightClick,
		onFontStyleClick,
		onTextDecorationChange,
		onTextTransformChange,
		onTextScriptChange,
		onTextAlignChange,
		onVerticalAlignChange,
		onListTypeChange,
	},
}: TextPanelProps) => (
	<>
		<Label value="messages.text_style" />
		<Section>
			<Select
				label="messages.font"
				value={fontFamily}
				isFontSelect={true}
				options={fontFamilyOptions}
				onChange={onFontFamilyChange}
			/>
			<ButtonsWrapper>
				<Button
					isActive={isBoldActive}
					isBold={true}
					onClick={onFontWeightClick}
				>
					B
				</Button>
				<Button
					isActive={isItalicActive}
					isItalic={true}
					onClick={onFontStyleClick}
				>
					I
				</Button>
				<Button
					isActive={isUnderlineActive}
					isUnderline={true}
					onClick={() => onTextDecorationChange(TEXT_DECORATION.UNDERLINE)}
				>
					U
				</Button>
				<Button
					isActive={isLineThroughActive}
					isLineThrough={true}
					onClick={() => onTextDecorationChange(TEXT_DECORATION.LINE_THROUGH)}
				>
					S
				</Button>
			</ButtonsWrapper>
			<Select
				label="messages.size"
				value={fontSize}
				options={fontSizeOptions}
				onChange={onFontSizeChange}
			/>
			{hasColorPicker &&
				<Select
					label="messages.color"
					value={color}
					isColorSelect={true}
					options={colorOptions}
					onChange={onColorChange}
				/>
			}
			<ButtonsWrapper>
				<Button
					isActive={isCapitalizeActive}
					onClick={() => onTextTransformChange(TEXT_TRANSFORM.CAPITALIZE)}
				>
					Aa
				</Button>
				<Button
					isActive={isUpperCaseActive}
					onClick={() => onTextTransformChange(TEXT_TRANSFORM.UPPERCASE)}
				>
					AA
				</Button>
				<Button
					isActive={isLowerCaseActive}
					onClick={() => onTextTransformChange(TEXT_TRANSFORM.LOWERCASE)}
				>
					aa
				</Button>
				<Button
					isActive={isSuperScriptActive}
					onClick={() => onTextScriptChange(TEXT_SCRIPT.SUPER)}
				>
					A<SuperScript>1</SuperScript>
				</Button>
				<Button
					isActive={isSubScriptActive}
					onClick={() => onTextScriptChange(TEXT_SCRIPT.SUB)}
				>
					A<SubScript>1</SubScript>
				</Button>
			</ButtonsWrapper>
		</Section>
		<Section>
			<ButtonsWrapper>
				<Button
					isActive={isTextAlignLeft}
					onClick={() => onTextAlignChange(TEXT_ALIGN.LEFT)}
				>
					{Utils.getSvgIcon('iconset_uprez_text-align-left')}
				</Button>
				<Button
					isActive={isTextAlignCenter}
					onClick={() => onTextAlignChange(TEXT_ALIGN.CENTER)}
				>
					{Utils.getSvgIcon('iconset_uprez_text-align-center')}
				</Button>
				<Button
					isActive={isTextAlignRight}
					onClick={() => onTextAlignChange(TEXT_ALIGN.RIGHT)}
				>
					{Utils.getSvgIcon('iconset_uprez_text-align-right')}
				</Button>
				<Button
					isActive={isTextAlignJustify}
					onClick={() => onTextAlignChange(TEXT_ALIGN.JUSTIFY)}
				>
					{Utils.getSvgIcon('iconset_uprez_text-align-justify')}
				</Button>
			</ButtonsWrapper>
			<ButtonsWrapper>
				<Button
					isActive={isVerticalAlignTop}
					onClick={() => onVerticalAlignChange('')}
				>
					{Utils.getSvgIcon('iconset_uprez_vertical_align_top')}
				</Button>
				<Button
					isActive={isVerticalAlignCenter}
					onClick={() => onVerticalAlignChange(VerticalAlignClassName.CENTER)}
				>
					{Utils.getSvgIcon('iconset_uprez_vertical_align_center')}
				</Button>
				<Button
					isActive={isVerticalAlignBottom}
					onClick={() => onVerticalAlignChange(VerticalAlignClassName.BOTTOM)}
				>
					{Utils.getSvgIcon('iconset_uprez_vertical_align_bottom')}
				</Button>
			</ButtonsWrapper>
		</Section>
		<Section>
			<Select
				label="messages.line_height"
				value={lineHeight}
				isLineHeightSelect={true}
				options={lineHeightOptions}
				onChange={onLineHeightChange}
			/>
			<Select
				label="messages.spacing"
				value={letterSpacing}
				options={letterSpacingOptions}
				onChange={onLetterSpacingChange}
			/>
		</Section>
		<Section>
			<Select
				label="messages.list_type"
				value={listType}
				isListTypeSelect={true}
				options={listTypeOptions}
				onChange={onListTypeChange}
			/>
		</Section>
	</>
);

export default TextPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/TextPanel/index.tsx